import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApolloQueryResult } from '@apollo/client/core';
import {
  LoginDocument,
  LoginInput,
  LoginWithSsoInput,
  LoginQuery,
  RefreshTokensDocument,
  RefreshTokensQuery,
  CreatePasswordResetRequestDocument,
  CreatePasswordResetInput,
  ConfirmPasswordResetInput,
  ConfirmPasswordResetRequestDocument,
  DataAccessService,
  FetchProfileQuery,
  FetchProfileDocument,
  CreatePasswordResetRequestMutation,
  ConfirmPasswordResetRequestMutation,
  User,
  LoginWithSsoDocument,
  LoginWithSsoQuery
} from '@tripointe/tripointe-cms/data-access';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { StorageService } from './storage.service';

enum AuthToken {
  ACCESS = 'accessToken',
  REFRESH = 'refreshToken'
}
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  user$: BehaviorSubject<User> = new BehaviorSubject(null);
  initialNavigationUrl: string | null;
  avatar$: BehaviorSubject<string> = new BehaviorSubject(null);
  constructor(
    private storageService: StorageService,
    private router: Router,
    private dataAccessService: DataAccessService
  ) {}

  loginWithSso(
    loginWithSsoInput: LoginWithSsoInput
  ): Observable<{ success: boolean; error?: string }> {
    return this.dataAccessService.get(LoginWithSsoDocument, { loginWithSsoInput }).pipe(
      switchMap((response: ApolloQueryResult<LoginWithSsoQuery>) => {
        const { success, error, tokens } = response.data.loginWithSso;
        if (success) {
          this.setTokens(tokens);

          return this.fetchProfile().pipe(
            map(() => {
              this.redirectAfterLogin();

              return { success, error };
            })
          );
        } else {
          return of({ success, error });
        }
      })
    );
  }

  login(loginInput: LoginInput): Observable<{ success: boolean; error?: string }> {
    return this.dataAccessService.get(LoginDocument, { loginInput }).pipe(
      switchMap((response: ApolloQueryResult<LoginQuery>) => {
        const { success, error, tokens } = response.data.login;
        if (success) {
          this.setTokens(tokens);

          return this.fetchProfile().pipe(
            map(() => {
              this.redirectAfterLogin();

              return { success, error };
            })
          );
        } else {
          return of({ success, error });
        }
      })
    );
  }

  fetchProfile(): Observable<{ success: boolean }> {
    return this.dataAccessService.get<FetchProfileQuery>(FetchProfileDocument).pipe(
      map((res: ApolloQueryResult<FetchProfileQuery>) => {
        const { success, user } = res.data.fetchProfile;
        if (success) {
          this.setUser(user as User);
          this.setAvatar(user.avatar);
        } else {
          this.logout();
        }

        return { success };
      })
    );
  }

  private setUser(user: User): void {
    this.user$.next(user);
  }

  private setTokens(result: any): void {
    this.storageService.save('accessToken', result.accessToken);
    this.storageService.save('refreshToken', result.refreshToken);
  }

  public setAvatar(avatarUrl: string): void {
    if (avatarUrl === '') {
      return;
    }
    this.avatar$.next(avatarUrl);
  }

  refreshTokens(): Observable<boolean> {
    const refreshToken = this.storageService.get('refreshToken');
    if (!refreshToken) {
      this.logout();
    }

    return this.dataAccessService.get(RefreshTokensDocument, { refreshToken }).pipe(
      map((response: ApolloQueryResult<RefreshTokensQuery>) => {
        const result = response.data.refreshTokens;
        if (result.success) {
          this.setTokens(result.tokens);

          return true;
        } else {
          return false;
        }
      })
    );
  }

  requestChangePassword(
    createPasswordResetRequest: CreatePasswordResetInput
  ): Observable<ApolloQueryResult<CreatePasswordResetRequestMutation>> {
    return this.dataAccessService.update(CreatePasswordResetRequestDocument, {
      createPasswordResetRequest
    });
  }

  confirmUpdatePassword(
    confirmPasswordResetInput: ConfirmPasswordResetInput
  ): Observable<ApolloQueryResult<ConfirmPasswordResetRequestMutation>> {
    return this.dataAccessService.update(ConfirmPasswordResetRequestDocument, {
      confirmPasswordResetInput
    });
  }

  checkIfTokenPresent(): boolean {
    return this.storageService.get(AuthToken.ACCESS) || this.storageService.get(AuthToken.REFRESH);
  }

  private redirectAfterLogin(): void {
    const redirectUrl = this.initialNavigationUrl || '/';
    this.initialNavigationUrl = null;
    this.router.navigate([redirectUrl]);
  }

  logout(): void {
    this.clearSessionData();
    this.router.navigate(['auth']);
  }

  clearSessionData(): void {
    this.storageService.remove('accessToken');
    this.storageService.remove('refreshToken');
    this.setUser(null);
    this.setAvatar(null);
  }

  getLoggedUserData(): User {
    return this.user$.getValue();
  }
}
